import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, BrainIcon, SparklesIcon, ScaleIcon, BookOpenIcon, CheckIcon, Loader2Icon, LayersIcon, PackageIcon } from 'lucide-react';
export function PersonalizedQuiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [answers, setAnswers] = useState({
    goal: '',
    experience: '',
    frequency: '',
    budget: ''
  });
  const goals = [{
    id: 'build-muscle',
    label: 'Build Muscle',
    icon: '💪',
    description: 'Gain strength & size',
    color: 'from-blue-500 to-blue-600',
    textColor: 'text-blue-600'
  }, {
    id: 'weight-loss',
    label: 'Weight Loss',
    icon: '⚖️',
    description: 'Burn fat & get lean',
    color: 'from-red-500 to-red-600',
    textColor: 'text-red-600'
  }, {
    id: 'increase-energy',
    label: 'Increase Energy',
    icon: '⚡',
    description: 'Boost performance',
    color: 'from-yellow-500 to-yellow-600',
    textColor: 'text-yellow-600'
  }, {
    id: 'general-health',
    label: 'General Health',
    icon: '❤️',
    description: 'Improve wellbeing',
    color: 'from-green-500 to-green-600',
    textColor: 'text-green-600'
  }, {
    id: 'womens-health',
    label: "Women's Health",
    icon: '🌸',
    description: 'Hormonal & daily support',
    color: 'from-pink-500 to-pink-600',
    textColor: 'text-pink-600'
  }, {
    id: 'mens-health',
    label: "Men's Health",
    icon: '🧔',
    description: 'Vitality & testosterone',
    color: 'from-indigo-500 to-indigo-600',
    textColor: 'text-indigo-600'
  }];
  const experienceLevels = [{
    id: 'beginner',
    label: 'Beginner',
    desc: 'New to supplements'
  }, {
    id: 'intermediate',
    label: 'Intermediate',
    desc: 'Some experience'
  }, {
    id: 'advanced',
    label: 'Advanced',
    desc: 'Experienced user'
  }];
  const frequencies = [{
    id: '2-3x',
    label: '2-3x per week',
    desc: 'Light activity'
  }, {
    id: '4-5x',
    label: '4-5x per week',
    desc: 'Active lifestyle'
  }, {
    id: '6+',
    label: '6+ times per week',
    desc: 'Intense training'
  }];
  const budgets = [{
    id: 'essential',
    label: 'Essential',
    desc: 'Basic needs (SAR 100-200)'
  }, {
    id: 'plus',
    label: 'Plus',
    desc: 'Core stack (SAR 200-400)'
  }, {
    id: 'premium',
    label: 'Premium',
    desc: 'Comprehensive (SAR 400+)'
  }];
  const services = [{
    id: 'ai-diet-planner',
    title: 'AI Nutrition & Diet Plan Creator',
    description: 'Get personalized nutrition targets and AI-generated meal plans tailored to your goals',
    icon: <BrainIcon className="w-8 h-8 text-white" data-id="element-5489" data-mp="wk8fxnpr4vg" />,
    color: 'from-purple-600 to-purple-700',
    accentColor: 'purple',
    shadowColor: 'shadow-purple-200',
    productImage: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400',
    link: '/nutrition-calculator'
  }, {
    id: 'bmi-calculator',
    title: 'BMI Calculator',
    description: 'Calculate your Body Mass Index and get personalized health recommendations',
    icon: <ScaleIcon className="w-8 h-8 text-white" data-id="element-5490" data-mp="jtbw0jaag65b" />,
    color: 'from-green-600 to-green-700',
    accentColor: 'green',
    shadowColor: 'shadow-green-200',
    productImage: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400',
    link: '/bmi-calculator'
  }, {
    id: 'glossary',
    title: 'Ingredient Glossary',
    description: 'Learn about supplement ingredients, their benefits and recommended products',
    icon: <BookOpenIcon className="w-8 h-8 text-white" data-id="element-5491" data-mp="3aw6x5j4e6ipu" />,
    color: 'from-blue-600 to-blue-700',
    accentColor: 'blue',
    shadowColor: 'shadow-blue-200',
    productImage: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400',
    link: '/ingredient-glossary'
  }, {
    id: 'stacking-guide',
    title: 'Stacking Guide',
    description: 'Learn how to combine supplements effectively for your specific fitness goals',
    icon: <LayersIcon className="w-8 h-8 text-white" data-id="element-5492" data-mp="3ovyf1uy14j94" />,
    color: 'from-orange-600 to-orange-700',
    accentColor: 'orange',
    shadowColor: 'shadow-orange-200',
    productImage: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=400',
    link: '/stacking-guide'
  }, {
    id: 'bundle-builder',
    title: 'Bundle Builder',
    description: 'Build your own supplement bundle and save up to 20% on the full stack',
    icon: <PackageIcon className="w-8 h-8 text-white" data-id="element-5493" data-mp="j01bpxzg0n1v" />,
    color: 'from-pink-600 to-pink-700',
    accentColor: 'pink',
    shadowColor: 'shadow-pink-200',
    productImage: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400',
    link: '/bundle-builder'
  }];
  const handleGoalSelect = (goalId: string) => {
    setAnswers({
      ...answers,
      goal: goalId
    });
    setIsQuizOpen(true);
    setStep(1);
  };
  const handleAnswer = (field: string, value: string) => {
    setAnswers({
      ...answers,
      [field]: value
    });
    if (step < 3) {
      setStep(step + 1);
    } else {
      // Final step completed
      setIsLoading(true);
      setTimeout(() => {
        navigate(`/quiz-results?goal=${answers.goal}`);
      }, 3000);
    }
  };
  const handleBack = () => {
    if (step === 1) {
      setIsQuizOpen(false);
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };
  return <div className="container mx-auto px-4 py-4 overflow-x-hidden" data-id="element-5494" data-mp="3ien8czs0908d">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] p-4 sm:p-6 md:p-8 text-white mb-6 sm:mb-8" data-id="element-5495" data-mp="3aeqn550eltk2">
        {/* Decorative elements */}
        <div className="absolute -top-16 -right-16 w-48 sm:w-64 h-48 sm:h-64 bg-white bg-opacity-5 rounded-full" data-id="element-5496" data-mp="3kcel03tb3h6g"></div>
        <div className="absolute top-32 -left-16 w-32 sm:w-48 h-32 sm:h-48 bg-white bg-opacity-5 rounded-full" data-id="element-5497" data-mp="3fbqwppwgpefx"></div>

        <div className="relative z-10" data-id="element-5498" data-mp="3tb06a6q5xaax">
          <div className="text-center mb-8" data-id="element-5499" data-mp="3pb7xbswux251">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 flex items-center justify-center gap-2" data-id="element-5500" data-mp="3pa6lvnbxidms">
              Choose Your Goal{' '}
              <SparklesIcon className="w-6 h-6 text-yellow-300" data-id="element-5501" data-mp="1y5l6866w0qg" />
            </h2>
            <p className="text-blue-100 text-lg" data-id="element-5502" data-mp="208asr3fjrun">
              and get <span className="font-bold text-yellow-300" data-id="element-5503" data-mp="gvy131fner56">15% OFF</span>{' '}
              your perfect supplement stack
            </p>
          </div>

          <AnimatePresence mode="wait" data-id="element-5504" data-mp="vhvau92zi0t7">
            {!isQuizOpen ? <motion.div key="goals" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto" data-id="element-5505" data-mp="vfqyr3uxlvhu">
                {goals.map(goal => <button key={goal.id} onClick={() => handleGoalSelect(goal.id)} className="bg-white rounded-xl p-4 sm:p-6 text-center transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl group" data-id="element-5506" data-mp="gmfefxexlstn">
                    <div className="text-4xl mb-3 group-hover:scale-110 transition-transform" data-id="element-5507" data-mp="3p2qnaxst0ydc">
                      {goal.icon}
                    </div>
                    <h3 className={`font-bold text-lg mb-1 ${goal.textColor}`} data-id="element-5508" data-mp="db32a9niyol0">
                      {goal.label}
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-5509" data-mp="98eum5ajrnpm">{goal.description}</p>
                  </button>)}
              </motion.div> : isLoading ? <motion.div key="loading" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="bg-white rounded-2xl p-8 max-w-2xl mx-auto text-center text-gray-900 shadow-xl" data-id="element-5510" data-mp="3vh7pujwu6pd6">
                <div className="flex flex-col items-center justify-center py-12" data-id="element-5511" data-mp="3cr82c1gn9ibv">
                  <div className="relative w-20 h-20 mb-6" data-id="element-5512" data-mp="3cp53qt1fegvj">
                    <div className="absolute inset-0 border-4 border-blue-100 rounded-full" data-id="element-5513" data-mp="37r3jw5w6edj0"></div>
                    <div className="absolute inset-0 border-4 border-[#1E2B55] rounded-full border-t-transparent animate-spin" data-id="element-5514" data-mp="32u3dtuzbtarw"></div>
                    <SparklesIcon className="absolute inset-0 m-auto w-8 h-8 text-[#1E2B55] animate-pulse" data-id="element-5515" data-mp="2sr7tatfzjgn0" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E2B55] mb-2" data-id="element-5516" data-mp="32nkltnzsnog6">
                    Finding your perfect supplements...
                  </h3>
                  <p className="text-gray-500" data-id="element-5517" data-mp="37lvx835qm70i">
                    Analyzing your profile to build the optimal stack.
                  </p>
                </div>
              </motion.div> : <motion.div key="quiz" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto text-gray-900 shadow-xl relative" data-id="element-5518" data-mp="2exzzqw8cgb2o">
                <button onClick={handleBack} className="absolute top-6 left-6 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-5519" data-mp="39n565tajwxns">
                  <ArrowLeftIcon className="w-6 h-6" data-id="element-5520" data-mp="3jmvz3qjgr4lr" />
                </button>

                <div className="flex justify-center gap-2 mb-8 mt-2" data-id="element-5521" data-mp="3nnfvut04k67d">
                  {[1, 2, 3].map(i => <div key={i} className={`h-2 rounded-full transition-all duration-300 ${step >= i ? 'w-8 bg-[#1E2B55]' : 'w-2 bg-gray-200'}`} data-id="element-5522" data-mp="2qy6vnkuxqn6u" />)}
                </div>

                <AnimatePresence mode="wait" data-id="element-5523" data-mp="34o1zy5iu4le8">
                  {step === 1 && <motion.div key="step1" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} data-id="element-5524" data-mp="34q521nx9i4zb">
                      <h3 className="text-2xl font-bold text-center mb-6" data-id="element-5525" data-mp="1vmh3feuvashf">
                        What's your experience level?
                      </h3>
                      <div className="space-y-3" data-id="element-5526" data-mp="1lpci1h8uj11o">
                        {experienceLevels.map(level => <button key={level.id} onClick={() => handleAnswer('experience', level.id)} className="w-full p-4 rounded-xl border-2 border-gray-100 hover:border-[#1E2B55] hover:bg-blue-50 transition-all text-left flex items-center justify-between group" data-id="element-5527" data-mp="1gydxu6kqlit5">
                            <div data-id="element-5528" data-mp="y01lyd2fxutv">
                              <div className="font-bold text-lg" data-id="element-5529" data-mp="y12zco6xiter">
                                {level.label}
                              </div>
                              <div className="text-gray-500 text-sm" data-id="element-5530" data-mp="oescyit4ygd9">
                                {level.desc}
                              </div>
                            </div>
                            <div className="w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#1E2B55] flex items-center justify-center" data-id="element-5531" data-mp="eg2r6ey4cbrq">
                              {answers.experience === level.id && <div className="w-3 h-3 bg-[#1E2B55] rounded-full" data-id="element-5532" data-mp="1c20fwu33ddc4" />}
                            </div>
                          </button>)}
                      </div>
                    </motion.div>}

                  {step === 2 && <motion.div key="step2" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} data-id="element-5533" data-mp="1q19ob7l2335j">
                      <h3 className="text-2xl font-bold text-center mb-6" data-id="element-5534" data-mp="3paj91r8yyhhb">
                        How often do you workout?
                      </h3>
                      <div className="space-y-3" data-id="element-5535" data-mp="2wl3w2vxqueir">
                        {frequencies.map(freq => <button key={freq.id} onClick={() => handleAnswer('frequency', freq.id)} className="w-full p-4 rounded-xl border-2 border-gray-100 hover:border-[#1E2B55] hover:bg-blue-50 transition-all text-left flex items-center justify-between group" data-id="element-5536" data-mp="2rm9gs3yzj83e">
                            <div data-id="element-5537" data-mp="gtg5di9bpi8e">
                              <div className="font-bold text-lg" data-id="element-5538" data-mp="gsdaz0cskxnq">
                                {freq.label}
                              </div>
                              <div className="text-gray-500 text-sm" data-id="element-5539" data-mp="1g4ewcfg05h6k">
                                {freq.desc}
                              </div>
                            </div>
                            <div className="w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#1E2B55] flex items-center justify-center" data-id="element-5540" data-mp="1l649q1qp66xt">
                              {answers.frequency === freq.id && <div className="w-3 h-3 bg-[#1E2B55] rounded-full" data-id="element-5541" data-mp="1z30yzyr63gcl" />}
                            </div>
                          </button>)}
                      </div>
                    </motion.div>}

                  {step === 3 && <motion.div key="step3" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} data-id="element-5542" data-mp="2dthyhck8rw6u">
                      <h3 className="text-2xl font-bold text-center mb-6" data-id="element-5543" data-mp="3ocbgn11fjwiw">
                        What's your budget preference?
                      </h3>
                      <div className="space-y-3" data-id="element-5544" data-mp="pu4t2cl8gd4t">
                        {budgets.map(budget => <button key={budget.id} onClick={() => handleAnswer('budget', budget.id)} className="w-full p-4 rounded-xl border-2 border-gray-100 hover:border-[#1E2B55] hover:bg-blue-50 transition-all text-left flex items-center justify-between group" data-id="element-5545" data-mp="kwczle4xwiyi">
                            <div data-id="element-5546" data-mp="13te46xul0h1x">
                              <div className="font-bold text-lg" data-id="element-5547" data-mp="13sckk4zi2m8l">
                                {budget.label}
                              </div>
                              <div className="text-gray-500 text-sm" data-id="element-5548" data-mp="331nmavfghmk5">
                                {budget.desc}
                              </div>
                            </div>
                            <div className="w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#1E2B55] flex items-center justify-center" data-id="element-5549" data-mp="2tb1alcav1g6q">
                              {answers.budget === budget.id && <div className="w-3 h-3 bg-[#1E2B55] rounded-full" data-id="element-5550" data-mp="2jeg3mlfa5v09" />}
                            </div>
                          </button>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}